import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from 'src/Contexts/AuthContext';
import { useToast } from 'src/Contexts/ToastContext';

import './Profile.css';

const MIN_NEW_PASSWORD_LENGTH = 8;

const Profile = () => {
  const { t } = useTranslation();
  const { currentUser, changePassword } = useAuth();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const newTooShort = newPassword.length > 0 && newPassword.length < MIN_NEW_PASSWORD_LENGTH;
  const confirmMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    !submitting &&
    currentPassword.length > 0 &&
    newPassword.length >= MIN_NEW_PASSWORD_LENGTH &&
    newPassword === confirmPassword;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      reset();
      toast.success(t('profile.successToast'));
    } catch (err: any) {
      // The backend gives us a 400 with a specific message for "current
      // password incorrect" and "must differ". Surface those directly when
      // we have them; otherwise fall back to a generic error.
      const detail = err?.apiError?.message || err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('profile.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="profile-page">
      <h2 className="profile-title">{t('profile.title')}</h2>
      {currentUser && <p className="profile-email">{currentUser.email}</p>}

      <form
        onSubmit={(e) => void handleSubmit(e)}
        aria-label={t('profile.title')}
        className="profile-form"
      >
        <div className="profile-field">
          <input
            value={currentPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCurrentPassword(e.target.value)}
            type="password"
            id="profile-current"
            required
            aria-required="true"
            placeholder=" "
            autoComplete="current-password"
          />
          <label htmlFor="profile-current">{t('profile.currentPassword')}</label>
        </div>

        <div className="profile-field">
          <input
            value={newPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
            type="password"
            id="profile-new"
            required
            aria-required="true"
            placeholder=" "
            autoComplete="new-password"
            minLength={MIN_NEW_PASSWORD_LENGTH}
          />
          <label htmlFor="profile-new">{t('profile.newPassword')}</label>
          {newTooShort && (
            <span className="profile-hint" role="alert">
              {t('profile.errorTooShort', { min: MIN_NEW_PASSWORD_LENGTH })}
            </span>
          )}
        </div>

        <div className="profile-field">
          <input
            value={confirmPassword}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
            type="password"
            id="profile-confirm"
            required
            aria-required="true"
            placeholder=" "
            autoComplete="new-password"
          />
          <label htmlFor="profile-confirm">{t('profile.confirmPassword')}</label>
          {confirmMismatch && (
            <span className="profile-hint" role="alert">
              {t('profile.errorMismatch')}
            </span>
          )}
        </div>

        <button type="submit" disabled={!canSubmit} className="profile-submit">
          {submitting ? t('profile.submitting') : t('profile.submit')}
        </button>
      </form>
    </div>
  );
};

export default Profile;
